import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BeneficiaryPreferencesFormModel } from './form-group/beneficiary-preferences-form.model';
import { BaseFormComponent } from '../../../../core/components/base-form.component';
import { BeneficiaryPreferencesFormBuilder } from './form-group/beneficiary-preferences.form.builder';
import { NotyfikacjeBeneficjenta } from '../../../../shared/models/notyfikacje-beneficjenta';
import { Checkbox } from 'primeng/checkbox';
import { Card } from 'primeng/card';
import { Button } from 'primeng/button';
import { UserApi } from '../../../../shared/services/api/user.api';
import { FormsUtils } from '../../../../shared/utils/forms.utils';
import { catchError, EMPTY, filter, finalize, tap } from 'rxjs';
import { ToastSeverity } from '../../../../shared/enums/toast-severity.enum';
import { SpinnerService } from '../../../../core/services/spinner.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpResponseService } from '../../../../core/services/http-error-response.service';
import { Router } from '@angular/router';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { DialogService } from 'primeng/dynamicdialog';
import { AuthService } from '../../../../core/services/auth.service';
import { MessageService } from 'primeng/api';
import { SessionCounterService } from '../../../../core/services/session-counter.service';

@Component({
  selector: 'app-form-beneficiary-settings-beneficiary-preferences',
  imports: [ReactiveFormsModule, Checkbox, Card, Button],
  templateUrl:
    './form-beneficiary-settings-beneficiary-preferences.component.html',
  styleUrl:
    './form-beneficiary-settings-beneficiary-preferences.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormBeneficiarySettingsBeneficiaryPreferencesComponent
  extends BaseFormComponent<BeneficiaryPreferencesFormModel>
  implements OnInit
{
  public beneficiaryPreferences = input.required<NotyfikacjeBeneficjenta>();
  readonly #destroyRef = inject(DestroyRef);
  readonly #userApi = inject(UserApi);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #messageService = inject(MessageService);
  readonly #spinnerService = inject(SpinnerService);
  readonly #router = inject(Router);
  readonly #dialogService = inject(DialogService);
  readonly #authService = inject(AuthService);
  readonly #sessionCounterService = inject(SessionCounterService);

  protected override createFormGroup(): FormGroup<BeneficiaryPreferencesFormModel> {
    return BeneficiaryPreferencesFormBuilder.createFormGroup(
      this.beneficiaryPreferences(),
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  protected save(): void {
    FormsUtils.markAllControlsAsTouchedAndDirty(
      Object.values(this.formGroup().controls),
    );
    if (this.formGroup().valid) {
      this.#spinnerService.show();
      this.#userApi
        .saveUserNotificationSettings$(
          BeneficiaryPreferencesFormBuilder.toModel(this.formGroup()),
        )
        .pipe(
          tap(() => {
            this.#httpResponseService.showSuccessMessage(
              'Poprawnie zapisano ustawienia',
            );
          }),
          catchError((err) => {
            this.#httpResponseService.showErrorMessage(
              err,
              'Wystąpił błąd podczas zapisu ustawień',
            );
            return EMPTY;
          }),
          takeUntilDestroyed(this.#destroyRef),
          finalize(() => {
            this.#spinnerService.hide();
          }),
        )
        .subscribe();
    }
  }

  protected openDeactivateDialog(): void {
    const ref = this.#dialogService.open(ConfirmDialogComponent, {
      closable: false,
      header: `Dezaktywacja konta spowoduje automatyczne wylogowanie użytkownika, brak możliwości logowania do konta oraz brak dostępu do dokumentów.
Odzyskanie dostępu możliwe będzie po ponownej rejestracji użytkownika do eWniosków.
Czy potwierdzasz dezaktywację konta?`,
      closeOnEscape: false,
      modal: true,
      dismissableMask: false,
      width: '60vw',
      inputValues: { confirmButtonLabel: 'Potwierdź' },
    });
    if (ref) {
      ref.onClose
        .pipe(
          filter(Boolean),
          tap(() => {
            const userId = this.#authService.getUser()?.id;

            if (!userId) {
              this.#messageService.add({
                severity: ToastSeverity.ERROR,
                summary:
                  'Wystąpił błąd przy identyfikacji użytkownika — nie można dezaktywować konta',
              });
              return;
            }

            this.#userApi
              .deactivateAccount$(userId)
              .pipe(
                tap(() => {
                  this.#httpResponseService.showSuccessMessage(
                    'Konto zostało dezaktywowane pomyślnie',
                  );
                  this.#sessionCounterService.endSession();
                }),
                catchError((err) => {
                  this.#httpResponseService.showErrorMessage(
                    err,
                    'Nie udało się dezaktywować konta',
                  );
                  return EMPTY;
                }),
                takeUntilDestroyed(this.#destroyRef),
                finalize(() => {
                  this.#spinnerService.hide();
                }),
              )
              .subscribe();
          }),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  protected goToHomepage(): void {
    this.#router.navigate(['/']);
  }
}
